<template>
  <v-navigation-drawer fixed app v-model="drawer.open" v-if="auth.check()"
    :style="[drawer.open ? {'width': '300px !important'} : {'width': '0px !important'}]"
  >
    <v-list-item class="logo-wrapper" height="100">
      <img src="../assets/logo-van-amerongen.svg" />
    </v-list-item>

    <v-list open-strategy="multiple" density="compact" nav >
      <template v-for="(item, i) in visibleItems" :key="item.text">
        {{ item.disabled }}
        <v-list-group
          v-if="item.children"
          :value="item"
        >
        <!-- :active-class="activeRoute === item.children.find(routeName) ? 'primary' : ''" -->
          <template v-slot:activator="{ props }">
            <v-list-item
              class="py-0 mb-0"
              v-bind="props"
              @click.stop
              :color="isOnChildrenRoute(item) ? 'primary' : ''"
            >
              <template v-slot:prepend>
                <v-icon>{{ item.icon }}</v-icon>
              </template>
              <div class="custom-title" >{{ item.text }}</div>
            </v-list-item>
          </template>
          <v-list-item
            class="py-0 mb-0"
            v-for="(child, childIndex) in visibleChildren(item)"
            :key="childIndex"
            :to="{ name: child.routeName, params: child.params }"
            :ripple="true"
            v-bind:disabled="child.disabled || !child.routeName"
            color="primary"
          >

            <span class="custom-title" >{{ child.text }}</span>
          </v-list-item>
        </v-list-group>

        <v-list-item
          v-else-if="!item.children && item.key !== 'search'"
          class="py-0 mb-0"
          :to="{ name: item.routeName, params: item.params }"
          :ripple="true"
          :prepend-icon="item.icon"
          :exact="item.exact !== false"
          v-bind:disabled="item.disabled || !item.routeName"
          color="primary"
        >
          <span class="custom-title" >{{ item.text }}</span>
        </v-list-item>

        <v-list-item
          v-if="item.key === 'search'"
          class="py-0 mb-0"
          :ripple="true"
          :prepend-icon="item.icon"
          color="primary"
        >
          <v-text-field
            type="text"
            variant="underlined"
            placeholder="Search"
            class="py-0 mb-0"
            @keydown="$emit('open-search', true)"
          />
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useAuth } from '@/utils/auth';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import emitArray from '@/utils/emit'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAuth()
const emit = defineEmits(emitArray.value)

type ChildType = {
  text: string;
  routeName: string;
  params?: { job_id?: any };
  disabled?: boolean;
  permission?: string;
  permissions?: string[];
};

defineProps({
  drawer: {
    type: Object,
    default() {
      return {
        open: true,
      }
    },
  },
})

const getNavigationItems = () => {
  const baseItems = [
    {
      icon: 'mdi-home',
      text: t('dashboard'),
      routeName: 'Dashboard',
      exact: true,
      // initialize variable to remove the errors
      disabled: null,
      params: {},
    },
    {
      icon: 'mdi-traffic-cone',
      text: t('job', 2),
      routeName: 'JobsList',
      permission: 'view jobs',
      permissions: [
        'view job equipment',
        'view job documents',
        'view job notes',
        'view job damage and loss reports',
        'view job print-outs',
        'view job additional costs',
        'view job cost overview',
        'view job financial documents',
      ],
      children: [
        { text: t('allJobs'), routeName: 'JobsList' },
        { text: t('jobCreate'), routeName: 'JobsCreate', permission: 'create jobs' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-car-estate',
      text: t('workOrder', 2),
      routeName: 'WorkOrdersList',
      permission: 'view work orders',
      children: [
        { text: t('allWorkOrders'), routeName: 'WorkOrdersList' },
        {
          text: t('workOrderCreate'),
          routeName: 'WorkOrdersCreate',
          params: { job_id: null as any },
          permission: 'create work orders',
        },
        { text: t('workOrderCategory', 2), routeName: 'WorkOrderCategoriesList', permission: 'view work order categories' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-briefcase-variant',
      text: t('client', 2),
      routeName: 'ClientsList',
      permission: 'view clients',
      children: [
        { text: t('allClients'), routeName: 'ClientsList' },
        { text: t('clientCreate'), routeName: 'ClientsCreate', permission: 'create clients' },
        { text: t('clientsArchived'), routeName: 'ClientsArchivedList' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-account-multiple',
      routeName: 'EmployeesList',
      text: t('employee', 2),
      permission: 'view employees',
      children: [
        { text: t('allEmployees'), routeName: 'EmployeesList' },
        { text: t('allArchivedEmployees'), routeName: 'EmployeesArchivedList' },
        { text: t('employeeCreate'), routeName: 'EmployeesCreate', permission: 'create employees' },
        {
          text: t('weekOverview'),
          routeName: 'EmployeesWeekOverview',
        },
        {
          text: t('leaveAndHolidays'),
          routeName: 'EmployeesTimeoff',
        },
      ] as ChildType[],
    },
    {
      icon: 'mdi-car-hatchback',
      routeName: 'EquipmentList',
      text: t('equipment', 2),
      permission: 'view equipment',
      children: [
        { text: t('allEquipments'), routeName: 'EquipmentList' },
        { text: t('equipmentCreate'), routeName: 'EquipmentCreate', permission: 'create equipment' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-truck',
      routeName: 'VehiclesList',
      text: t('vehicle', 2),
      permission: 'view vehicles',
      children: [
        { text: t('allVehicles'), routeName: 'VehiclesList' },
        { text: t('vehicleCreate'), routeName: 'VehiclesCreate', permission: 'create vehicles' },
        { text: t('vehicleType', 2), routeName: 'VehicleTypesList' },
        { text: t('vehicleTypeCreate'), routeName: 'VehicleTypesCreate', permission: 'create vehicles' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-face-man',
      routeName: 'UsersList',
      text: t('user', 2),
      permission: 'view users',
      children: [
        { text: t('allUsers'), routeName: 'UsersList' },
        { text: t('userCreate'), routeName: 'UsersCreate', permission: 'create users' },
        { text: t('roleManage'), routeName: 'RolesList', permission: 'view roles' },
        { text: t('roleCreate'), routeName: 'RolesCreate', permission: 'create role' },
      ] as ChildType[],
    },
    {
      icon: 'mdi-home-variant',
      text: t('branch', 2),
      routeName: 'BranchesList',
      permission: 'view branches',
      children: [
        { text: t('allBranches'), routeName: 'BranchesList' },
        { text: t('branchCreate'), routeName: 'BranchesCreate', permission: 'create branches' },
      ] as ChildType[],
    },
  ];

  baseItems.push({
    icon: 'mdi-book-open-page-variant',
    text: t('rentalSuppliers', 2),
    routeName: 'RentalSuppliersList',
    permission: 'view rental suppliers',
    children: [
      { text: t('allRentalSuppliers'), routeName: 'RentalSuppliersList' },
      { text: t('rentalSupplierCreate'), routeName: 'RentalSuppliersCreate', permission: 'create rental suppliers' },
    ] as ChildType[],
  });

  baseItems.push({
    icon: 'mdi-book-open-page-variant',
    text: t('inventory', 2),
    routeName: 'EquipmentItemsList',
    permission: 'view inventory',
    children: [
      { text: t('allEquipmentItems'), routeName: 'EquipmentItemsList' },
      { text: t('equipmentItemCreate'), routeName: 'EquipmentItemsCreate', permission: 'create inventory' },
    ] as ChildType[],
  });

  baseItems.push({
    icon: 'mdi-book-open-page-variant',
    text: t('reports', 2),
    routeName: 'Reports',
    permission: 'view work in progress reports',
    children: [
      { text: t('workInProgress'), routeName: 'WorkInProgressReportsList' },
    ] as ChildType[],
  });

  baseItems.push({
      icon: 'mdi-magnify',
      key: 'search',
    });

  return baseItems;
};

const activeRoute = ref(null as any)
const items = ref(getNavigationItems())

const hasAccess = (item: Record<string, any>) => {
  if (item.permission && auth.check(item.permission)) return true
  if (item.permissions && item.permissions.some((permission: string) => auth.check(permission))) return true
  return !item.permission && !item.permissions
}

const visibleItems = computed(() => items.value.filter((item: Record<string, any>) => hasAccess(item)))

const visibleChildren = (item: Record<string, any>) => {
  if (!item.children) return []
  return item.children.filter((child: ChildType) => hasAccess(child))
}

watch(route ,() => {
  setActiveRoute()
})

onMounted(() => {
  setActiveRoute()
})

const setActiveRoute = () => {
  items.value.forEach((item: Record<string, any>) => {
    const itemRouteName = item.routeName

    if (router.currentRoute.value.matched.length == 0) {
      activeRoute.value = null
      return
    }

    const currentRouteName = route.name

    if (itemRouteName === currentRouteName) {
      activeRoute.value = currentRouteName
      return
    }
    if (
      itemRouteName === route.name
    ) {
      activeRoute.value = route.name
    } else if(item.children) {
      item.children.forEach((child: Record<string, undefined>) => {
        if(child.routeName === currentRouteName) {
          activeRoute.value = route.name
        }
      })
    }
  })
}
const isOnChildrenRoute = (item: any) => {
  let result = false
  item.children.forEach((r: Record<string, undefined>) => {
    if(r.routeName === activeRoute.value) {
      result =  true;
    }
  })
  return result;
}
</script>

<style scoped lang="scss">
.text--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.logo-wrapper {
  padding: 20px 0;
  text-align: center;
  border-bottom: 1px solid #ddd;
}
.logo-wrapper {
  img {
    width: 38%;
  }
}
.custom-title {
  text-transform: capitalize;
  font-weight: 700 !important;
}
.list__group--active {
  background: #f5f5f5;
}
</style>
